import React from "react";
import { createInertiaApp } from "@inertiajs/react";
import { createRoot } from "react-dom/client";
import { invoke, setRunningBuild } from "../lib/bridge";
import { ThemeProvider } from "../hooks/useTheme";
import { NotificationsProvider } from "../hooks/useNotifications";
import { unlockAudio } from "../hooks/useNotificationSound";

// Record the build hash of the bundle actually running (the entry module URL carries it), so the
// app can detect when a newer build has been deployed and offer to reload (see check_update).
setRunningBuild(import.meta.url);

// --- Global crash reporting (dedup: report each unique message once per session) ---
const reported = new Set<string>();
function reportCrashOnce(message: string, stack: string) {
  if (reported.has(message)) return;
  reported.add(message);
  invoke("report_crash", { message, stack }).catch(() => {});
}
window.onerror = (msg, source, line, col, error) => {
  try {
    reportCrashOnce(error?.message || String(msg), error?.stack || `${source}:${line}:${col}`);
  } catch {
    /* ignore */
  }
};
window.onunhandledrejection = (event) => {
  try {
    const err = event.reason;
    reportCrashOnce(err?.message || String(err), err?.stack || "");
  } catch {
    /* ignore */
  }
};

// --- Unlock audio on first user interaction (still needed for mobile browsers) ---
const unlock = () => {
  unlockAudio();
  document.removeEventListener("touchstart", unlock, true);
  document.removeEventListener("touchend", unlock, true);
  document.removeEventListener("click", unlock, true);
};
document.addEventListener("touchstart", unlock, true);
document.addEventListener("touchend", unlock, true);
document.addEventListener("click", unlock, true);

// Disable pinch-to-zoom. iOS Safari ignores the viewport `user-scalable=no`
// directive outside standalone PWA mode, so block the gesture/multi-touch events too.
document.addEventListener("gesturestart", (e) => e.preventDefault());
document.addEventListener(
  "touchmove",
  (e) => {
    if (e.touches.length > 1) e.preventDefault();
  },
  { passive: false },
);

// Register the offline-shell service worker (no-op if unsupported).
if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch(() => {});
  });
}

// Dev-only: expose the Lebowski test-family seed for screenshots + Playwright.
// `import.meta.env.DEV` is statically false in production, so this block (and the
// seed module it pulls in) is dead-code-eliminated from the prod bundle.
if (import.meta.env.DEV) {
  import("../lib/seed").then(({ seedLebowskis }) => {
    (window as unknown as { __seedLebowskis: typeof seedLebowskis }).__seedLebowskis = seedLebowskis;
    if (new URLSearchParams(location.search).get("seed") === "lebowskis") {
      seedLebowskis()
        .then(() => location.replace("/"))
        .catch((e) => console.error("seedLebowskis failed:", e));
    }
  });

  // Test-only hooks for the multi-device Playwright e2e (see e2e/helpers.ts): drive
  // the bridge directly from page.evaluate(), and build the local doc snapshots a
  // keeper hands off on invite/resync. Same dev-only gate as the seed above.
  (window as unknown as { __invoke: typeof invoke }).__invoke = invoke;
  import("../lib/snapshots").then(({ buildSnapshots }) => {
    (window as unknown as { __buildSnapshots: typeof buildSnapshots }).__buildSnapshots = buildSnapshots;
  });
}

const pages = import.meta.glob("../pages/**/*.tsx", { eager: true });

createInertiaApp({
  resolve: (name) => {
    const page = pages[`../pages/${name}.tsx`];
    if (!page) throw new Error(`Inertia page not found: ${name}`);
    return page as { default: React.ComponentType };
  },
  setup({ el, App, props }) {
    createRoot(el).render(
      <React.StrictMode>
        <ThemeProvider>
          <NotificationsProvider>
            <App {...props} />
          </NotificationsProvider>
        </ThemeProvider>
      </React.StrictMode>,
    );
  },
});
